class QuickAdd extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    this.variantId = this.getAttribute('variant-id');
    this.originalText = this.querySelector('.product-card__action-btn-text');
    this.spinner = this.querySelector('.product-card__action-btn-spinner');
    
    if (this.button) {
      this.button.addEventListener('click', this.onClick);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.onClick);
    }
  }

  updateVariant(variantId, available) {
    this.variantId = variantId;
    this.setAttribute('variant-id', variantId);

    if (this.button && this.originalText) {
      if (available === 'true' || available === true) {
        this.button.removeAttribute('disabled');
        this.originalText.textContent = window.themeVariables?.strings?.addToCart || 'Add to cart';
      } else {
        this.button.setAttribute('disabled', 'disabled');
        this.originalText.textContent = window.themeVariables?.strings?.soldOut || 'Sold out';
      }
    }
  }

  setUnavailable() {
    this.variantId = null;
    this.removeAttribute('variant-id');

    if (this.button && this.originalText) {
      this.button.setAttribute('disabled', 'disabled');
      this.originalText.textContent = window.themeVariables?.strings?.unavailable || 'Unavailable';
    }
  }

  async onClick(event) {
    event.preventDefault();
    if (this.button.hasAttribute('disabled') || !this.variantId) return;
    
    this.button.setAttribute('disabled', 'disabled');
    if (this.spinner && this.originalText) {
      this.originalText.setAttribute('hidden', '');
      this.spinner.removeAttribute('hidden');
    }
    
    try {
      if (!window.CartAPI) {
        throw new Error('CartAPI not available');
      }
      
      const response = await window.CartAPI.addLineItem(this.variantId, 1);
      
      if (response.status && response.status !== 200) {
        throw new Error('Failed to add item to cart');
      }
      
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    } catch (error) {
      console.error('Quick Add Error:', error);
      alert(window.themeVariables?.strings?.cartError || 'There was an error adding this item to your cart.');
    } finally {
      this.button.removeAttribute('disabled');
      if (this.spinner && this.originalText) {
        this.spinner.setAttribute('hidden', '');
        this.originalText.removeAttribute('hidden');
      }
      
      // Keep disabled if it was sold out, but re-evaluate based on current variantId.
      // Wait, we can just trigger a custom re-eval if needed, but for now we just remove disabled.
      // Actually, if we successfully added, it might be sold out now, but that's complex to track.
      // For now, if we reach finally, we assume it's still available.
    }
  }
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAdd);
}

class ProductCardInlineVariants extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.card = this.closest('.product-card');
    this.quickAdd = this.card ? this.card.querySelector('quick-add') : null;
    this.priceContainer = this.card ? this.card.querySelector('.price-list') : null;
    this.moneyFormat = this.dataset.moneyFormat || window.themeVariables?.moneyFormat || '${{amount}}';

    const variantsJson = this.querySelector('[data-product-card-variants]');
    try {
      this.variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];
    } catch (error) {
      console.error('Failed to parse product card variant JSON', error);
      this.variants = [];
    }

    this.optionGroups = Array.from(this.querySelectorAll('.product-card__option-group'));
    this.selectedOptions = this.getSelectedOptions();

    this.addEventListener('click', this.onClick);
    this.updateOptionAvailability();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  getSelectedOptions() {
    return this.optionGroups.map((group) => {
      const pressed = group.querySelector('[aria-pressed="true"]');
      return pressed ? pressed.dataset.optionValue : null;
    });
  }

  onClick(event) {
    const button = event.target.closest('button[data-option-position]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();

    const index = parseInt(button.dataset.optionPosition, 10) - 1;
    if (Number.isNaN(index) || index < 0 || !this.optionGroups[index]) return;

    this.selectedOptions[index] = button.dataset.optionValue;

    this.optionGroups[index].querySelectorAll('button[data-option-position]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn === button ? 'true' : 'false');
    });

    const variant = this.resolveVariant();
    this.updatePrice(variant);
    this.updateMedia(variant);
    this.updateQuickAdd(variant);
    this.updateOptionAvailability();
  }

  resolveVariant() {
    if (this.selectedOptions.some((value) => value == null)) return null;

    return this.variants.find((variant) => (
      variant.options.every((option, index) => option === this.selectedOptions[index])
    )) || null;
  }

  updateOptionAvailability() {
    this.optionGroups.forEach((group, index) => {
      group.querySelectorAll('button[data-option-position]').forEach((btn) => {
        const candidate = [...this.selectedOptions];
        candidate[index] = btn.dataset.optionValue;

        const match = this.variants.find((variant) => (
          variant.options.every((option, i) => option === candidate[i])
        ));

        if (!match) {
          btn.dataset.availability = 'impossible';
        } else if (!match.available) {
          btn.dataset.availability = 'sold-out';
        } else {
          btn.dataset.availability = 'available';
        }
        btn.setAttribute('aria-disabled', btn.dataset.availability === 'available' ? 'false' : 'true');
      });
    });
  }

  formatMoney(cents) {
    if (cents == null) return '';
    const value = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  updatePrice(variant) {
    if (!this.priceContainer || !variant) return;

    const strings = window.themeVariables?.strings || {};
    const isSale = variant.compare_at_price > variant.price;
    const priceFormatted = this.formatMoney(variant.price);

    this.priceContainer.innerHTML = '';

    if (isSale) {
      const salePrice = document.createElement('span');
      salePrice.className = 'price-list__sale';
      salePrice.innerHTML = `<span class="visually-hidden">${strings.salePrice || 'Sale price'}</span>${priceFormatted}`;

      const comparePrice = document.createElement('span');
      comparePrice.className = 'price-list__compare';
      comparePrice.innerHTML = `<span class="visually-hidden">${strings.regularPrice || 'Regular price'}</span><s class="text-subdued line-through">${this.formatMoney(variant.compare_at_price)}</s>`;

      this.priceContainer.appendChild(salePrice);
      this.priceContainer.appendChild(comparePrice);
    } else {
      const regularPrice = document.createElement('span');
      regularPrice.className = 'price-list__regular';
      regularPrice.innerHTML = `<span class="visually-hidden">${strings.regularPrice || 'Regular price'}</span>${priceFormatted}`;
      this.priceContainer.appendChild(regularPrice);
    }
  }

  updateMedia(variant) {
    if (!this.card || !variant) return;

    // A variant without its own image shows the product image, never the previously chosen variant's image.
    const media = variant.featured_media || (this.dataset.productMediaSrc
      ? { src: this.dataset.productMediaSrc, srcset: this.dataset.productMediaSrcset || '' }
      : null);
    if (!media) return;

    const primaryImage = this.card.querySelector('.product-card__image--primary');
    if (primaryImage) {
      primaryImage.srcset = media.srcset;
      primaryImage.src = media.src;
    }
  }

  updateQuickAdd(variant) {
    if (!this.quickAdd) return;

    if (!variant) {
      this.quickAdd.setUnavailable();
    } else {
      this.quickAdd.updateVariant(variant.id, variant.available);
    }
  }
}

if (!customElements.get('product-card-inline-variants')) {
  customElements.define('product-card-inline-variants', ProductCardInlineVariants);
}

/**
 * <quick-buy-trigger> - the product card's "Choose options" / "Options" button. Fetches the product's quick-buy
 * section (sections/quick-buy.liquid) and shows it in an <es-modal>: a dialog on larger screens, a bottom sheet on
 * phones. Variant selection and Add to cart are the locked product engine inside that section.
 *
 *   - The modal is built fresh each time, AFTER its content is in place. ESModal finds its <dialog> when it connects;
 *     the previous version connected an empty modal and filled it afterwards, so open() had no dialog to show (and
 *     still took the scroll lock).
 *   - Its stylesheets are awaited before it opens, so it never flashes unstyled.
 *   - A successful add opens the cart drawer; the dialog then closes so the drawer is visible (a modal dialog sits in
 *     the top layer, above any drawer). Watched only while this dialog is open.
 */
class QuickBuyTrigger extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    this.originalText = this.querySelector('.product-card__action-btn-text');
    this.spinner = this.querySelector('.product-card__action-btn-spinner');
    this.productUrl = this.button ? this.button.dataset.productUrl : null;

    if (this.button && this.productUrl) {
      this.button.addEventListener('click', this.onClick);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.onClick);
    }
  }

  /* Busy, not disabled: disabling the focused button would drop keyboard focus to the page while the dialog loads,
     and the dialog would then have nothing to return focus to. Repeat presses are ignored while loading. */
  setLoading(loading) {
    this.loading = loading;
    this.button.setAttribute('aria-disabled', loading ? 'true' : 'false');
    this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (this.spinner && this.originalText) {
      this.originalText.toggleAttribute('hidden', loading);
      this.spinner.toggleAttribute('hidden', !loading);
    }
  }

  async onClick(event) {
    event.preventDefault();
    if (this.loading) return;
    this.setLoading(true);

    try {
      const url = this.productUrl + (this.productUrl.includes('?') ? '&' : '?') + 'section_id=quick-buy';
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Quick buy request failed (${response.status})`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('#shopify-section-quick-buy');
      if (!content || !content.querySelector('dialog')) throw new Error('Quick buy section missing');

      document.getElementById('quick-buy-modal')?.remove();

      const modal = document.createElement('es-modal');
      modal.id = 'quick-buy-modal';
      modal.appendChild(content);
      document.body.appendChild(modal);

      await QuickBuyTrigger.stylesheetsReady(modal);
      this.closeOnCartOpen(modal);
      modal.open(this.button);
    } catch (error) {
      console.error('Quick buy:', error);
      window.location.href = this.productUrl;
    } finally {
      this.setLoading(false);
    }
  }

  closeOnCartOpen(modal) {
    const drawer = document.querySelector('es-drawer[id^="cart-drawer"]');
    const dialog = modal.querySelector('dialog');
    if (!drawer || !dialog || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      if (drawer.hasAttribute('open')) modal.close();
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    dialog.addEventListener('close', () => observer.disconnect(), { once: true });
  }

  static stylesheetsReady(root) {
    const links = Array.from(root.querySelectorAll('link[rel="stylesheet"]')).filter((link) => !link.sheet);
    const loaded = links.map(
      (link) =>
        new Promise((resolve) => {
          link.addEventListener('load', resolve, { once: true });
          link.addEventListener('error', resolve, { once: true });
        })
    );
    // Never wait more than a moment: an open dialog with late styles beats no dialog.
    return Promise.race([Promise.all(loaded), new Promise((resolve) => setTimeout(resolve, 1500))]);
  }
}

if (!customElements.get('quick-buy-trigger')) {
  customElements.define('quick-buy-trigger', QuickBuyTrigger);
}

/**
 * <product-wishlist-button> - the Soft panel card's heart. Marks a product as a favourite on this device: the state is
 * aria-pressed on the button, remembered in localStorage by product handle, and kept in step across every card for
 * the same product on the page through one document event. Storage can be unavailable (private mode, blocked site
 * data); the button then still toggles for the current page. A wishlist app can read the same list.
 */
class ProductWishlistButton extends HTMLElement {
  static storageKey = 'es-wishlist';

  static read() {
    try {
      const list = JSON.parse(window.localStorage.getItem(ProductWishlistButton.storageKey));
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return ProductWishlistButton.memory || [];
    }
  }

  static write(list) {
    ProductWishlistButton.memory = list;
    try {
      window.localStorage.setItem(ProductWishlistButton.storageKey, JSON.stringify(list));
    } catch (error) {
      /* Storage unavailable: keep the in-memory list for this page. */
    }
  }

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.render = this.render.bind(this);
  }

  connectedCallback() {
    this.handle = this.getAttribute('handle');
    this.button = this.querySelector('button');
    if (!this.handle || !this.button) return;

    this.button.addEventListener('click', this.onClick);
    document.addEventListener('es-wishlist:change', this.render);
    this.render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener('es-wishlist:change', this.render);
  }

  onClick(event) {
    event.preventDefault();
    const list = ProductWishlistButton.read().filter((handle) => handle !== this.handle);
    if (this.button.getAttribute('aria-pressed') !== 'true') list.push(this.handle);
    ProductWishlistButton.write(list);
    document.dispatchEvent(new CustomEvent('es-wishlist:change', { detail: { handle: this.handle } }));
  }

  render() {
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', ProductWishlistButton.read().includes(this.handle) ? 'true' : 'false');
  }
}

if (!customElements.get('product-wishlist-button')) {
  customElements.define('product-wishlist-button', ProductWishlistButton);
}

/**
 * <card-options-sheet> - the Soft panel card's options, opened INSIDE the card: the card dims softly and a rounded panel
 * rises from its bottom edge. A presentation of the same product engine the Standard card's dialog uses, never a copy:
 *
 *   - Content  the quick-buy section (sections/quick-buy.liquid) is fetched as for the dialog, and only its
 *              <product-info> block is mounted here, so variant resolution, price, availability, quantity rules and
 *              Add to cart are the locked ProductInfo / ProductForm. Its ids get a per-sheet suffix: the option radios
 *              and quantity find their form by document id, so a second copy (another sheet, the Standard dialog)
 *              must never share them.
 *   - Local    no page backdrop and no page scroll lock. The rest of the card is made inert while open, so neither
 *              focus nor a stray click reaches the obscured title, heart or badges.
 *   - One      opening a sheet closes any other open sheet.
 *   - Closes   on the close button, the dimmed card area, Escape, or a successful add (the cart drawer opening).
 *              Focus returns to the Options control without scrolling the page.
 *   - Options  is a link to the product page (works without JavaScript); this element turns it into a button.
 */
class CardOptionsSheet extends HTMLElement {
  static active = null;
  static count = 0;

  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerKeyDown = this.onTriggerKeyDown.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.isOpen = false;
    this.loading = false;
    this.observer = null;
    this.hideTimer = null;
  }

  connectedCallback() {
    this.card = this.closest('.product-card--soft');
    this.trigger = this.card?.querySelector('[data-card-options-open]');
    this.panel = this.querySelector('[data-card-options-panel]');
    this.content = this.querySelector('[data-card-options-content]');
    this.closers = Array.from(this.querySelectorAll('[data-card-options-close]'));
    this.productUrl = this.dataset.productUrl;
    if (!this.card || !this.trigger || !this.panel || !this.content || !this.productUrl) return;

    CardOptionsSheet.count += 1;
    this.uid = `card-options-${CardOptionsSheet.count}`;
    this.id = this.uid;
    this.trigger.setAttribute('role', 'button');
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', this.uid);
    this.spinner = this.trigger.querySelector('.product-card__action-btn-spinner');
    this.label = this.trigger.querySelector('.product-card__action-btn-text');

    // Always start closed, even when re-connected with markup copied from an open sheet.
    this.isOpen = false;
    this.hidden = true;
    delete this.dataset.state;
    this.content.replaceChildren();
    this.setObscured(false);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeyDown);
    this.addEventListener('keydown', this.onKeyDown);
    this.closers.forEach((el) => el.addEventListener('click', this.onCloseClick));
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.trigger?.removeEventListener('keydown', this.onTriggerKeyDown);
    this.removeEventListener('keydown', this.onKeyDown);
    this.closers?.forEach((el) => el.removeEventListener('click', this.onCloseClick));
    this.observer?.disconnect();
    this.observer = null;
    clearTimeout(this.hideTimer);
    if (CardOptionsSheet.active === this) CardOptionsSheet.active = null;
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Links activate on Enter only; a button also activates on Space. */
  onTriggerKeyDown(event) {
    if (event.key === ' ') {
      event.preventDefault();
      this.trigger.click();
    }
  }

  onTriggerClick(event) {
    event.preventDefault();
    if (this.loading) return;
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  onCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.close();
    }
  }

  setLoading(loading) {
    this.loading = loading;
    this.trigger.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (this.spinner && this.label) {
      this.label.toggleAttribute('hidden', loading);
      this.spinner.toggleAttribute('hidden', !loading);
    }
  }

  async open() {
    if (CardOptionsSheet.active && CardOptionsSheet.active !== this) {
      CardOptionsSheet.active.close({ restoreFocus: false });
    }
    this.setLoading(true);

    try {
      const url = this.productUrl + (this.productUrl.includes('?') ? '&' : '?') + 'section_id=quick-buy';
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Options request failed (${response.status})`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = html.querySelector('#shopify-section-quick-buy');
      const info = root && root.querySelector('product-info');
      if (!info) throw new Error('Options content missing');

      await CardOptionsSheet.adoptStylesheets(root);
      this.mount(info);
      this.show();
    } catch (error) {
      console.error('Card options:', error);
      window.location.href = this.productUrl;
    } finally {
      this.setLoading(false);
    }
  }

  /* Gives the fetched block's ids - and every attribute that points at one - this sheet's suffix, then mounts it.
     <product-info> builds its own lookups (#product-form-..., #price-...) from data-section, so it follows. */
  mount(info) {
    const sectionId = info.dataset.section || 'quick-buy';
    const suffix = `${sectionId}-${this.uid}`;
    const attributes = ['id', 'for', 'form', 'aria-labelledby', 'aria-controls', 'aria-describedby'];

    info.dataset.section = suffix;
    info.querySelectorAll(attributes.map((name) => `[${name}]`).join(',')).forEach((el) => {
      attributes.forEach((name) => {
        const value = el.getAttribute(name);
        if (value && value.includes(sectionId)) el.setAttribute(name, value.split(sectionId).join(suffix));
      });
    });

    this.content.replaceChildren(info);
    const title = info.querySelector('[id^="QuickBuyTitle-"]');
    if (title) this.panel.setAttribute('aria-labelledby', title.id);
  }

  show() {
    clearTimeout(this.hideTimer);
    this.hidden = false;
    this.isOpen = true;
    CardOptionsSheet.active = this;
    this.setObscured(true);
    this.trigger.setAttribute('aria-expanded', 'true');
    this.watchCart();

    // One frame in the closed state first, so the panel slides up from the card's bottom edge.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.dataset.state = 'open';
        // The panel itself (labelled by the product title): the sheet opens at its top and is announced by name.
        this.panel.focus({ preventScroll: true });
      });
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (CardOptionsSheet.active === this) CardOptionsSheet.active = null;
    delete this.dataset.state;
    this.observer?.disconnect();
    this.observer = null;
    this.setObscured(false);
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger.focus({ preventScroll: true });

    // Hidden and emptied once the slide-down finishes, so its ids and form leave the page.
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(
      () => {
        this.hidden = true;
        this.content.replaceChildren();
        this.panel.removeAttribute('aria-labelledby');
      },
      this.reducedMotion ? 0 : 300
    );
  }

  /* Everything in the card except this sheet: out of the tab order and unclickable while the sheet covers it. */
  setObscured(obscured) {
    Array.from(this.card.children).forEach((child) => {
      if (child !== this) child.toggleAttribute('inert', obscured);
    });
  }

  /* A successful add opens the cart drawer (ProductForm); the sheet then closes and the drawer takes focus. */
  watchCart() {
    const drawer = document.querySelector('es-drawer[id^="cart-drawer"]');
    if (!drawer || typeof MutationObserver === 'undefined') return;
    this.observer = new MutationObserver(() => {
      if (drawer.hasAttribute('open')) this.close({ restoreFocus: false });
    });
    this.observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  /* The fetched section links its stylesheets; add any the page does not have yet, and wait briefly for them. */
  static adoptStylesheets(root) {
    const pending = [];
    root.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      const present = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some((existing) => existing.getAttribute('href') === href);
      if (present) return;
      const clone = document.createElement('link');
      clone.rel = 'stylesheet';
      clone.href = href;
      pending.push(
        new Promise((resolve) => {
          clone.addEventListener('load', resolve, { once: true });
          clone.addEventListener('error', resolve, { once: true });
        })
      );
      document.head.appendChild(clone);
    });
    return Promise.race([Promise.all(pending), new Promise((resolve) => setTimeout(resolve, 1500))]);
  }
}

if (!customElements.get('card-options-sheet')) {
  customElements.define('card-options-sheet', CardOptionsSheet);
}

/**
 * <product-card-slider> - the Soft panel card's swipeable images (snippets/product-card-soft.liquid). The row is a
 * native scroll-snap container, so swipe, trackpad and RTL need no script; this only keeps the current dot in step
 * with the scroll position and lets a click on a dot move to its image (for mouse users, who cannot swipe).
 */
class ProductCardSlider extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.frame = null;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-card-slides]');
    this.dots = Array.from(this.querySelectorAll('[data-card-dot]'));
    if (!this.track || this.dots.length < 2) return;

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  /* In RTL scrollLeft runs from 0 to negative, so the distance from the start is its absolute value. */
  get currentIndex() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    return Math.min(this.dots.length - 1, Math.round(Math.abs(this.track.scrollLeft) / width));
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const index = this.currentIndex;
      this.dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
    });
  }

  onDotClick(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.cardDot, 10);
    if (Number.isNaN(index)) return;

    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: direction * index * this.track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('product-card-slider')) {
  customElements.define('product-card-slider', ProductCardSlider);
}
